import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Drop, Lightning, ShieldCheck, Factory,
  TestTube, Mountains, Atom, Snowflake,
} from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import { productCategories } from "../data/products";
import ClientMarquee from "../components/ClientMarquee";
import SEO from "../components/SEO";

const Home = () => {
  const { t } = useLang();

  const stats = [
    { v: t.home.stat1, l: t.home.stat1Label, icon: TestTube },
    { v: t.home.stat2, l: t.home.stat2Label, icon: Mountains },
    { v: t.home.stat3, l: t.home.stat3Label, icon: Lightning },
    { v: t.home.stat4, l: t.home.stat4Label, icon: ShieldCheck },
  ];

  const industries = [
    {
      key: "copper",
      title: t.home.copper,
      desc: t.home.copperDesc,
      img: "https://images.pexels.com/photos/2101140/pexels-photo-2101140.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      size: "md:col-span-7 md:row-span-2",
    },
    {
      key: "cobalt",
      title: t.home.cobalt,
      desc: t.home.cobaltDesc,
      img: "https://images.pexels.com/photos/35497312/pexels-photo-35497312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      size: "md:col-span-5",
    },
    {
      key: "gold",
      title: t.home.gold,
      desc: t.home.goldDesc,
      img: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1000&q=80",
      size: "md:col-span-5",
    },
    {
      key: "water",
      title: t.home.water,
      desc: t.home.waterDesc,
      img: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?auto=format&fit=crop&w=1000&q=80",
      size: "md:col-span-12 md:row-auto",
    },
  ];

  const catIcon = { size_reduction: Atom, separation: Drop, flotation: Lightning, dewatering: Snowflake, refining: Factory, spares: ShieldCheck };

  return (
    <>
      <SEO
        title="Industrial Chemicals & Mining Solutions"
        description="Masika Chem SARL is a Lubumbashi-based supplier of industrial chemicals, reagents and grinding media for the mining industry across the Greater Katanga region, DRC."
        path="/"
      />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-neutral-900" data-testid="home-hero">
        <img
          src="https://images.pexels.com/photos/2101140/pexels-photo-2101140.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Aerial mining operation in DRC"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-grid-dark opacity-40" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 lg:px-10 pb-20 pt-32">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-3 mb-8">
              <span className="w-10 h-px bg-[#E85A1F]" />
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#E85A1F]">
                {t.home.heroOverline}
              </span>
            </div>
            <h1
              className="text-white font-extrabold leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5.2vw, 4.6rem)" }}
            >
              {t.home.heroTitle}
            </h1>
            <p className="mt-7 text-lg text-neutral-300 max-w-xl leading-relaxed">
              {t.home.heroSub}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/products"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A00D25] text-white px-7 py-4 font-semibold transition-colors"
              >
                {t.home.ctaPrimary}
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white px-7 py-4 font-semibold transition-colors"
              >
                {t.home.ctaSecondary}
                <ArrowUpRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ClientMarquee />

      {/* STATS */}
      <section className="bg-white" data-testid="home-stats">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 border border-neutral-200">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className={`p-8 lg:p-10 ${i < stats.length - 1 ? "lg:border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i === 0 || i === 1 ? "border-b" : ""} border-neutral-200`}
                  data-testid={`stat-${i}`}
                >
                  <Icon size={26} weight="duotone" className="text-[#C8102E] mb-6" />
                  <div className="text-5xl lg:text-6xl font-black tracking-tighter text-[#E85A1F] leading-none">
                    {s.v}
                  </div>
                  <div className="mt-3 text-sm text-neutral-600 font-medium">{s.l}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-[#F7F7F5] py-24" data-testid="home-industries">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C8102E]" />
                <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                  {t.home.industriesOverline}
                </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-neutral-900 leading-[1.05] tracking-tight">
                {t.home.industriesTitle}
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-neutral-600 leading-relaxed">{t.home.industriesSub}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {industries.map((ind) => (
              <Link
                to="/industries"
                key={ind.key}
                data-testid={`industry-card-${ind.key}`}
                className={`group relative overflow-hidden bg-black ${ind.size} min-h-[260px] md:min-h-[300px]`}
              >
                <img
                  src={ind.img}
                  alt={ind.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-7">
                  <h3 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight">
                    {ind.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-200 max-w-md">{ind.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#E85A1F]">
                    {t.common.learnMore} <ArrowRight size={14} weight="bold" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="bg-white py-24" data-testid="home-product-categories">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-6">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C8102E]" />
                <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                  {t.home.productsOverline}
                </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-neutral-900 leading-[1.05] tracking-tight">
                {t.home.productsTitle}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-lg text-neutral-600 leading-relaxed">{t.home.productsSub}</p>
            </div>
          </div>

          <div className="border-t border-neutral-200">
            {productCategories.map((cat, i) => {
              const Icon = catIcon[cat.key] || Factory;
              return (
                <Link
                  to="/products"
                  key={cat.key}
                  data-testid={`product-cat-${cat.key}`}
                  className="group grid grid-cols-12 items-center gap-6 py-7 border-b border-neutral-200 hover:bg-neutral-50 transition-colors px-2"
                >
                  <div className="col-span-1 text-xs font-bold tracking-wider text-neutral-400">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-1">
                    <Icon size={22} weight="duotone" className="text-[#C8102E]" />
                  </div>
                  <div className="col-span-7 md:col-span-6">
                    <h3 className="text-xl md:text-2xl font-bold text-neutral-900 group-hover:text-[#C8102E] transition-colors">
                      {t.products.categories[cat.key]}
                    </h3>
                  </div>
                  <div className="hidden md:block md:col-span-3 text-sm text-neutral-500">
                    {cat.products.length} products
                  </div>
                  <div className="col-span-3 md:col-span-1 flex justify-end">
                    <ArrowUpRight size={22} weight="bold" className="text-neutral-400 group-hover:text-[#C8102E] group-hover:rotate-45 transition-all duration-300" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BLOCK */}
      <section className="bg-[#C8102E] relative overflow-hidden" data-testid="home-cta-block">
        <div className="absolute inset-0 bg-grid-dark opacity-20" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-[1.05] tracking-tight">
                {t.home.ctaBlockTitle}
              </h2>
              <p className="mt-5 text-lg text-white/85 max-w-2xl">{t.home.ctaBlockSub}</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/contact"
                data-testid="home-cta-block-button"
                className="inline-flex items-center gap-2 bg-white text-[#C8102E] hover:bg-neutral-100 px-7 py-4 font-semibold transition-colors"
              >
                {t.home.ctaBlockBtn}
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
